<template>
  <ValidationObserver ref="form" v-slot="{ handleSubmit, invalid }" slim>
    <form class="auth-form" @submit.prevent="handleSubmit(nextStep)">
      <!--switch login-->
      <div v-if="layout === 'B' && menu === 'button'" class="mb-4 text-center">
        <span class="me-2">{{ $t('component.SignUpModal.login') }}</span>
        <a
          href="#"
          class="text-decoration-underline auth-form-link"
          @click.prevent="$emit('switchCategory', 'login')"
          >{{ $t('component.AuthModal.list.login') }}</a
        >
      </div>

      <Steppers class="mb-4" :step="stepNum" />

      <transition :name="transitionName" mode="out-in">
        <div v-if="stepNum === 1" key="signUpStep1">
          <!-- email -->
          <div class="mb-4">
            <label class="form-label required">
              {{ $t('common.profile.email') }}
            </label>
            <div class="row gx-3 align-items-center">
              <div class="col">
                <ValidationProvider
                  ref="regEmailRef"
                  v-slot="{ valid, invalid, dirty }"
                  vid="regEmail"
                  :name="$t('common.profile.email')"
                  rules="required|email"
                  slim
                >
                  <input
                    v-model="regEmail"
                    :class="{
                      'is-valid': valid,
                      'is-invalid': (dirty && invalid) || isOneTimeEmail
                    }"
                    name="regEmail"
                    type="email"
                    class="form-control"
                    autofocus
                    @blur="checkIsOneTimeEmail"
                  />
                </ValidationProvider>
              </div>
              <div v-if="hasEmailVerifyCode" class="col-auto">
                <button
                  v-if="waitingToResendTime <= 0"
                  type="button"
                  class="btn"
                  :class="customBtnPrimaryClass"
                  :disabled="
                    !regEmail ||
                      ($refs.regEmailRef && $refs.regEmailRef.errors[0]) ||
                      showCaptcha
                  "
                  @click.prevent="sendVerifyCode"
                >
                  {{
                    $t('component.SignUpModal.regEmailVerifyCode.buttons.send')
                  }}
                </button>
                <button
                  v-else
                  type="button"
                  class="btn"
                  :class="customBtnPrimaryClass"
                  disabled
                >
                  {{
                    $t(
                      'component.SignUpModal.regEmailVerifyCode.buttons.waitToResend',
                      [waitingToResendTime]
                    )
                  }}
                </button>
              </div>
            </div>
            <div v-if="isOneTimeEmail" class="d-block invalid-feedback">
              {{ $t('validate.oneTimeEmail') }}
            </div>
            <div v-else class="form-text">
              {{ $t('component.SignUpModal.regEmail.text') }}
            </div>
          </div>
          <!-- email 認證 -->
          <div v-if="hasEmailVerifyCode" class="mb-4">
            <label for="regEmailVerifyCode" class="form-label required">
              {{ $t('component.SignUpModal.regEmailVerifyCode.label') }}
            </label>
            <input
              id="regEmailVerifyCode"
              v-model="regEmailVerifyCode"
              name="regEmailVerifyCode"
              type="text"
              class="form-control"
              :class="{
                'is-valid': isEmailVerifyCodeCorrect,
                'is-invalid':
                  isEmailVerifyCodeCorrect !== null && !isEmailVerifyCodeCorrect
              }"
              :readonly="
                !regEmail ||
                  ($refs.regEmailRef && $refs.regEmailRef.errors[0]) ||
                  isOneTimeEmail
              "
            />
            <div class="form-text">
              {{ $t('component.SignUpModal.regEmailVerifyCode.text') }}
            </div>
          </div>
          <div v-if="showCaptcha" class="mb-4">
            <vue-hcaptcha
              ref="hcaptcha"
              sitekey="e32699e9-2c27-41fe-bd34-243d32d51251"
              :language="locale"
              @verify="captchaVerify"
              @expired="refreshCaptcha"
              @reset="refreshCaptcha"
              @error="errorHandler"
              @rendered="$refs.hcaptcha.reset()"
            />
          </div>
          <!-- 下一步 -->
          <div class="mb-4">
            <button
              type="button"
              class="btn rounded-pill d-block w-100"
              :disabled="
                invalid || (!!hasEmailVerifyCode && !isEmailVerifyCodeCorrect)
              "
              :class="customBtnPrimaryClass"
              @click.prevent="nextStep"
            >
              {{ $t('component.SignUpModal.actions.next') }}
            </button>
          </div>

          <!--switch login-->
          <div v-if="layout === 'A' && menu === 'button'" class="text-center">
            <span class="me-2">{{ $t('component.SignUpModal.login') }}</span>
            <a
              href="#"
              class="text-decoration-underline auth-form-link"
              @click.prevent="$emit('switchCategory', 'login')"
              >{{ $t('component.AuthModal.list.login') }}</a
            >
          </div>

          <!--oauth-->
          <div
            v-if="hasTelegramOauth || hasGoogleOauth || hasLineOauth"
            class="divider my-4"
          >
            or
          </div>
          <div
            v-if="hasTelegramOauth || hasGoogleOauth || hasLineOauth"
            class="d-grid gap-3"
          >
            <button
              v-if="hasTelegramOauth"
              class="btn btn-outline-gray btn-oauth rounded-pill"
              @click.prevent="signUpWithTelegram"
            >
              <img
                :src="
                  `https://ik.imagekit.io/1pzxddx3qqv/Z54IV3/static/18/social-media-telegram.png`
                "
                alt="btn-oauth-telegram"
              />
              <span v-html="$t('common.oauth.login', ['Telegram'])"></span>
            </button>
            <button
              v-if="hasGoogleOauth"
              class="btn btn-outline-gray btn-oauth rounded-pill"
              @click.prevent="signUpWithGoogle"
            >
              <img
                :src="
                  `https://ik.imagekit.io/1pzxddx3qqv/Z54IV3/static/18/social-media-google.png`
                "
                alt="btn-oauth-google"
              />
              <span v-html="$t('common.oauth.login', ['Google'])"></span>
            </button>
            <button
              v-if="hasLineOauth"
              class="btn btn-outline-gray btn-oauth rounded-pill"
              @click.prevent="signUpWithLine"
            >
              <img
                :src="
                  `https://ik.imagekit.io/1pzxddx3qqv/Z54IV3/static/18/social-media-line.png`
                "
                alt="btn-oauth-line"
              />
              <span v-html="$t('common.oauth.login', ['Line'])"></span>
            </button>
          </div>
        </div>

        <div v-else-if="stepNum === 2" key="signUpStep2">
          <!-- 使用者名稱 -->
          <div class="mb-4">
            <label for="regUsername" class="form-label required">
              {{ $t('common.profile.username') }}
            </label>
            <ValidationProvider
              ref="regUsernameRef"
              v-slot="{ valid, invalid, dirty }"
              vid="regUsername"
              :name="$t('common.profile.username')"
              :rules="customUsernameRule"
              slim
            >
              <input
                id="regUsername"
                v-model="regUsername"
                :class="{
                  'is-valid': valid,
                  'is-invalid':
                    (dirty && invalid) ||
                    (isUsernameValid !== null && !isUsernameValid)
                }"
                name="regUsername"
                type="text"
                class="form-control"
                @blur="hasEmailVerifyCode && checkUsername()"
              />
            </ValidationProvider>
            <div v-if="!usernameInvalidMessage" class="form-text">
              {{ $t('component.SignUpModal.regUsername.text') }}
            </div>
            <div
              v-else
              class="d-block invalid-feedback"
              v-html="usernameInvalidMessage"
            ></div>
          </div>
          <!-- 密碼 -->
          <ValidationProvider
            ref="regPasswordRef"
            v-slot="{ valid, invalid, dirty, errors }"
            vid="regPassword"
            :name="$t('common.profile.password')"
            :rules="{
              required: true,
              regex: new RegExp(/^(?![0-9]+$)(?![a-zA-Z]+$)[0-9A-Za-z]{6,16}$/),
              is_not: regUsername
            }"
            slim
            tag="div"
            class="mb-4"
          >
            <label class="form-label required">
              {{ $t('common.profile.password') }}
            </label>
            <input
              v-model="regPassword"
              :class="{
                'is-valid': valid,
                'is-invalid': dirty && invalid
              }"
              name="regPassword"
              type="password"
              class="form-control"
              autocomplete="new-password"
            />
            <div v-if="dirty && invalid" class="d-block invalid-feedback">
              {{ errors[0] }}
            </div>
            <div v-else class="form-text">
              {{ $t('component.SignUpModal.regPassword.text') }}
            </div>
          </ValidationProvider>
          <!-- 密碼再次驗證-->
          <ValidationProvider
            v-if="hasConfirmPassword"
            ref="regConfirmPasswordRef"
            v-slot="{ valid, invalid, dirty, errors }"
            vid="regConfirmPassword"
            :name="$t('common.profile.password')"
            rules="required|min:6|max:16|confirmed:regPassword"
            slim
            tag="div"
            class="mb-4"
          >
            <label class="form-label required">
              {{ $t('common.profile.confirmPassword') }}
            </label>
            <input
              v-model="regConfirmPassword"
              :class="{
                'is-valid': valid,
                'is-invalid': dirty && invalid
              }"
              name="regConfirmPassword"
              type="password"
              class="form-control"
              autocomplete="new-password"
            />
            <div v-if="dirty && invalid" class="d-block invalid-feedback">
              {{ errors[0] }}
            </div>
            <div v-else class="form-text">
              {{ $t('component.SignUpModal.regPassword.text') }}
            </div>
          </ValidationProvider>
          <!-- 下一步 -->
          <button
            type="button"
            class="btn rounded-pill d-block w-100"
            :disabled="invalid || (hasEmailVerifyCode && !isUsernameValid)"
            :class="customBtnPrimaryClass"
            @click.prevent="nextStep"
          >
            {{ $t('component.SignUpModal.actions.next') }}
          </button>
        </div>

        <div v-else key="signUpStep3">
          <!-- 姓名 -->
          <ValidationProvider
            v-if="hasActualName"
            ref="regActualNameRef"
            v-slot="{ valid, invalid, dirty, errors }"
            vid="regActualName"
            :name="$t('common.profile.realName')"
            :rules="{
              required: true,
              regex: new RegExp(/^[\u30A1-\u30FF\s]+$/)
            }"
            slim
            tag="div"
            class="mb-4"
          >
            <label class="form-label required">
              {{ $t('common.profile.realName') }}
            </label>
            <input
              v-model="regActualName"
              :class="{
                'is-valid': valid,
                'is-invalid': dirty && invalid
              }"
              name="regActualName"
              type="text"
              class="form-control"
              :placeholder="
                $t('component.SignUpModal.regActualName.placeholder')
              "
            />
            <div v-if="dirty && invalid" class="d-block invalid-feedback">
              {{ errors[0] }}
            </div>
          </ValidationProvider>
          <!-- 電話 -->
          <div class="mb-4">
            <label class="form-label required">
              {{ $t('common.profile.tel') }}
            </label>
            <client-only>
              <IntelInput
                v-model="regTel"
                :placeholder="$t('component.SignUpModal.regTel.placeholder')"
                :country-list="intelCountries"
                @validate="isValid => (isRegTelValid = isValid)"
              ></IntelInput>
            </client-only>
          </div>
          <!-- 生日 -->
          <div class="mb-4">
            <label class="form-label required">
              {{ $t('common.profile.birthday') }}
            </label>
            <ValidationProvider
              v-if="!isMobile"
              ref="regBirthdayRef"
              v-slot="{ valid, invalid, dirty }"
              vid="regBirthday"
              :name="$t('common.profile.birthday')"
              rules="required|date"
              slim
            >
              <no-ssr>
                <date-picker
                  v-model="regBirthday"
                  :input-class="[
                    'form-control',
                    { 'is-valid': valid, 'is-invalid': dirty && invalid }
                  ]"
                  :placeholder="
                    $t('component.SignUpModal.regBirthday.placeholder')
                  "
                  :lang="langForDatePicker || 'en'"
                  value-type="format"
                  width="100%"
                  :disabled-date="
                    date =>
                      $dayjs()
                        .subtract(18, 'year')
                        .isBefore($dayjs(date))
                  "
                  default-value="1990-01-01"
                >
                  <i slot="icon-calendar" class="d-block km-icon-date"></i>
                </date-picker>
              </no-ssr>
            </ValidationProvider>
            <div v-else class="mx-datepicker position-relative">
              <ValidationProvider
                ref="regBirthdayRef"
                v-slot="{ valid, invalid, dirty }"
                vid="birthday"
                :name="$t('common.profile.birthday')"
                rules="required|date|date_after_year:18"
                slim
              >
                <input
                  id="birthday"
                  v-model="regBirthday"
                  :class="{
                    'is-valid': valid,
                    'is-invalid': dirty && invalid
                  }"
                  name="birthday"
                  type="date"
                  class="form-control"
                />
              </ValidationProvider>
              <i
                slot="icon-calendar"
                class="position-absolute km-icon-date"
              ></i>
            </div>
            <div class="form-text">
              {{ $t('component.SignUpModal.regBirthday.text') }}
            </div>
          </div>
          <!-- 同意 -->
          <div class="mb-4">
            <div class="form-check">
              <ValidationProvider
                ref="isAgreedRef"
                v-slot="{ valid, invalid, dirty }"
                vid="isAgreed"
                name="isAgreed"
                :rules="{ required: { allowFalse: false } }"
                slim
              >
                <input
                  id="isAgreed"
                  v-model="isAgreed"
                  name="isAgreed"
                  :class="customFormCheckInputClass"
                  type="checkbox"
                />
              </ValidationProvider>
              <label
                class="form-check-label"
                for="isAgreed"
                v-html="$t('component.SignUpModal.isAgreed.text')"
              >
              </label>
            </div>
          </div>
          <div class="mb-4">
            <div class="form-check">
              <ValidationProvider
                ref="isMarketedRef"
                v-slot="{ valid, invalid, dirty }"
                vid="isMarketed"
                name="isMarketed"
                :rules="{ required: { allowFalse: false } }"
                slim
              >
                <input
                  id="isMarketed"
                  v-model="isMarketed"
                  name="isMarketed"
                  :class="customFormCheckInputClass"
                  type="checkbox"
                />
              </ValidationProvider>
              <label
                class="form-check-label"
                for="isMarketed"
                v-html="$t('component.SignUpModal.isMarketed.text')"
              >
              </label>
            </div>
          </div>

          <!-- 上一步 ＆ 送出 -->
          <div class="row gx-3">
            <div class="col-6">
              <button
                type="button"
                class="btn btn-icon rounded-pill w-100"
                :class="customBtnOutlinePrimaryClass"
                @click.prevent="prevStep"
              >
                <i class="km-icon-arrow-double-left"></i>
                <span>{{ $t('component.SignUpModal.actions.back') }}</span>
              </button>
            </div>
            <div class="col-6">
              <button
                :disabled="invalid || !isRegTelValid"
                type="submit"
                class="btn rounded-pill w-100"
                :class="customBtnPrimaryClass"
              >
                {{ $t('component.SignUpModal.actions.submit') }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </form>
  </ValidationObserver>
</template>

<script>
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  useStore,
  watch
} from '@nuxtjs/composition-api'
import { checkAxiosData } from 'fe-common'
import { catchError, concatMap, from, tap, filter } from 'rxjs'
import useVeeValidate from '../composables/useVeeValidate'
import useErrorHandler from '../composables/useErrorHandler'
import { handleMessages } from '../modules/client/utilities'
import useUser from '../composables/useUser'
import useLocale from '../composables/useLocale'
import useOauth from '../composables/useOauth'
import Steppers from './Steppers'
import IntelInput from './IntelInput.vue'

export default defineComponent({
  components: {
    IntelInput,
    Steppers
  },
  props: {
    layout: {
      type: String,
      default: 'A'
    },
    menu: {
      type: String,
      default: 'tabs'
    },
    intelCountries: {
      type: Array,
      default: () => []
    },
    hasConfirmPassword: {
      type: Boolean,
      default: false
    },
    hasActualName: {
      type: Boolean,
      default: false
    },
    hasEmailVerifyCode: {
      type: Boolean,
      default: true
    },
    customUsernameRule: {
      type: [String, Object],
      default: 'required|alpha_num|min:6|max:15'
    },
    // custom class
    customFormCheckInputClass: {
      type: String,
      default: 'form-check-input'
    },
    customBtnPrimaryClass: {
      type: String,
      default: 'btn-primary'
    },
    customBtnOutlinePrimaryClass: {
      type: String,
      default: 'btn-outline-primary'
    }
  },
  setup(props, context) {
    const store = useStore()

    const { locale, langForDatePicker } = useLocale()
    const { reset } = useVeeValidate(context)
    const {
      errorHandler,
      errorHandler$,
      errorDisplayHandler,
      logging
    } = useErrorHandler(context)
    const { checkIsAdult } = useUser(context)

    const isAgreed = ref(false)
    const isMarketed = ref(false)
    const showCaptcha = ref(false)
    const isOneTimeEmail = ref(false)
    const countDownTimer = ref(null)
    const isUsernameValid = ref(null)
    const usernameInvalidMessage = ref(null)
    const isEmailVerifyCodeCorrect = ref(null)
    const stepNum = ref(1)
    const waitingToResendTime = ref(0)
    const transitionName = ref('fade-from-left')
    const isRegTelValid = ref(false)

    const isMobile = computed(() => store.state.isMobile)
    const captchaToken = computed({
      get() {
        return store.state.authModule.captchaToken
      },
      set(value) {
        store.commit('authModule/CAPTCHA_TOKEN', value)
      }
    })
    const captchaEKey = computed({
      get() {
        return store.state.authModule.captchaEKey
      },
      set(value) {
        store.commit('authModule/CAPTCHA_E_KEY', value)
      }
    })
    const imageToken = computed({
      get() {
        return store.state.authModule.imageToken
      },
      set(value) {
        store.commit('authModule/IMAGE_TOKEN', value)
      }
    })
    const regUsername = computed({
      get() {
        return store.state.authModule.regUsername
      },
      set(value) {
        store.commit('authModule/REG_USERNAME', value)
      }
    })
    const regTel = computed({
      get() {
        return store.state.authModule.regTel
      },
      set(value) {
        store.commit('authModule/REG_TEL', value)
      }
    })
    const regPassword = computed({
      get() {
        return store.state.authModule.regPassword
      },
      set(value) {
        store.commit('authModule/REG_PASSWORD', value)
      }
    })
    const regEmail = computed({
      get() {
        return store.state.authModule.regEmail
      },
      set(value) {
        store.commit('authModule/REG_EMAIL', value)
      }
    })
    const regBirthday = computed({
      get() {
        return store.state.authModule.regBirthday
      },
      set(value) {
        store.commit('authModule/REG_BIRTHDAY', value)
      }
    })
    const regEmailVerifyCode = computed({
      get() {
        return store.state.authModule.regEmailVerifyCode
      },
      set(value) {
        store.commit('authModule/REG_EMAIL_VERIFY_CODE', value)
      }
    })
    const regCurrency = computed({
      get() {
        return store.state.authModule.regCurrency
      },
      set(value) {
        store.commit('authModule/REG_CURRENCY', value)
      }
    })
    const regConfirmPassword = computed({
      get() {
        return store.state.authModule.regConfirmPassword
      },
      set(value) {
        store.commit('authModule/REG_CONFIRM_PASSWORD', value)
      }
    })
    const regActualName = computed({
      get() {
        return store.state.authModule.regActualName
      },
      set(value) {
        store.commit('authModule/REG_ACTUAL_NAME', value)
      }
    })

    watch(regEmailVerifyCode, newVal => {
      if (newVal && newVal.length === 4) {
        checkEmailVerifyCode(newVal)
      }
    })

    onMounted(() => {
      refreshData()
    })
    onBeforeUnmount(() => {
      clearTimeout(countDownTimer.value)
    })

    function captchaVerify(token, ekey) {
      captchaToken.value = token
      captchaEKey.value = ekey
      showCaptcha.value = false
      sendVerifyCode()
    }
    function sendVerifyCode() {
      from(store.dispatch('startLoading'))
        .pipe(
          concatMap(() => from(store.dispatch('authModule/sendEmailVerify'))),
          tap(({ data }) => {
            waitingToResendTime.value = data
            countDown(1000)
          }),
          catchError(err =>
            errorHandler$(err, axiosErr => {
              if (axiosErr.code === 412381) {
                showCaptcha.value = true
                return true
              }
              return false
            })
          )
        )
        .subscribe({
          complete: () => store.dispatch('completeLoading')
        })
    }
    function refreshCaptcha() {
      captchaToken.value = null
      captchaEKey.value = null
    }
    function refreshData() {
      regUsername.value = null
      regTel.value = null
      regEmail.value = null
      regPassword.value = null
      regConfirmPassword.value = null
      regBirthday.value = null
      regEmailVerifyCode.value = null
      regActualName.value = null
      regCurrency.value = 'USD'
      isAgreed.value = false
      isMarketed.value = false

      isRegTelValid.value = false
      stepNum.value = 1
      showCaptcha.value = false
      isOneTimeEmail.value = false
      isUsernameValid.value = null
      usernameInvalidMessage.value = null
      isEmailVerifyCodeCorrect.value = null
      waitingToResendTime.value = 0
      transitionName.value = 'fade-from-left'
      imageToken.value = null

      clearTimeout(countDownTimer.value)
      refreshCaptcha()
      reset()
    }
    function countDown(period = 1000) {
      countDownTimer.value = setTimeout(_ => {
        if ((waitingToResendTime.value -= Math.floor(period / 1000) > 0)) {
          countDown(period)
        } else {
          waitingToResendTime.value = 0
        }
      }, period)
    }

    function nextStep() {
      if (stepNum.value === 3) {
        handleSignUp()
        return
      }
      transitionName.value = 'fade-from-left'
      stepNum.value++
    }

    function prevStep() {
      transitionName.value = 'fade-from-right'
      stepNum.value--
    }

    function handleSignUp() {
      window.dataLayer &&
        window.dataLayer.push({
          event: 'registerSubmitEvent',
          username: regUsername.value
        })

      signUp(signUpSuccess)
    }
    function signUp(successCallback) {
      if (!checkIsAdult(regBirthday.value)) {
        return
      }

      from(store.dispatch('startLoading'))
        .pipe(
          concatMap(() =>
            from(
              store.dispatch(
                'authModule/signUp',
                props.hasEmailVerifyCode ? 'V2' : ''
              )
            )
          ),
          filter(data =>
            checkAxiosData(data, {
              handleMessages: handleMessages({ store })
            })
          ),
          tap(({ result }) => {
            store.commit('authModule/TOKEN', result.token)
            store.commit('authModule/TOKEN_EXP', result.exp)
            store.commit('authModule/USER', result.user)
            successCallback()
          }),
          catchError(err =>
            errorHandler$(err, axiosErr => {
              if (axiosErr.code === 412384) {
                errorDisplayHandler(axiosErr).then(() => {
                  refreshData()
                })
                return true
              }
              return false
            })
          )
        )
        .subscribe({
          complete: () => store.dispatch('completeLoading')
        })
    }
    function signUpSuccess() {
      context.emit('switchCategory', null)

      context.emit('signUpSuccessAfter')
    }

    function checkIsOneTimeEmail() {
      if (!regEmail.value) {
        isOneTimeEmail.value = false
        return
      }

      from(store.dispatch('authModule/isOneTimeEmail'))
        .pipe(
          filter(data =>
            checkAxiosData(data, {
              handleMessages: handleMessages({
                store
              })
            })
          ),
          tap(({ data }) => {
            isOneTimeEmail.value = data
          }),
          catchError(err => {
            return errorHandler$(err)
          })
        )
        .subscribe()
    }
    function checkUsername() {
      setTimeout(() => {
        if (regUsername.value) {
          from(store.dispatch('authModule/checkUsername'))
            .pipe(
              filter(data =>
                checkAxiosData(data, {
                  handleMessages: handleMessages({
                    store
                  })
                })
              ),
              tap(({ data }) => {
                isUsernameValid.value = data === null ? true : data
                usernameInvalidMessage.value = null
              }),
              catchError(err => {
                isUsernameValid.value = false
                return errorHandler$(err, axiosErr => {
                  if (axiosErr.code === 412384) {
                    errorDisplayHandler(axiosErr).then(() => {
                      refreshData()
                    })
                    return true
                  }

                  usernameInvalidMessage.value =
                    axiosErr && axiosErr.message
                      ? axiosErr.message
                      : context.root.$t(
                          'component.mixin.mixinErrorHandler.method.errorHandler.other.networkError'
                        )
                  logging(axiosErr, ['message', 'code', 'statusCode', 'path'])
                  return true
                })
              })
            )
            .subscribe()
        }
      }, 0)
    }
    function checkEmailVerifyCode() {
      from(store.dispatch('authModule/checkEmailVerifyCodeV2'))
        .pipe(
          filter(data =>
            checkAxiosData(data, {
              handleMessages: handleMessages({
                store
              })
            })
          ),
          tap(({ headers, data }) => {
            isEmailVerifyCodeCorrect.value = data === null ? true : data
            imageToken.value = headers['6cfsezdad3']
          }),
          catchError(err => {
            isEmailVerifyCodeCorrect.value = false
            return errorHandler$(err)
          })
        )
        .subscribe()
    }

    const { signInWithTelegram, signInWithGoogle, signInWithLine } = useOauth(
      context
    )
    const hasTelegramOauth = computed(() => !!process.env.OAUTH_TELEGRAM_BOT_ID)
    function signUpWithTelegram() {
      signInWithTelegram(signUpSuccess)
    }
    const hasGoogleOauth = computed(() => !!process.env.OAUTH_GOOGLE_CLIENT_ID)
    function signUpWithGoogle() {
      signInWithGoogle(signUpSuccess)
    }
    const hasLineOauth = computed(
      () => !!process.env.OAUTH_LINE_ID && !!process.env.OAUTH_LINE_SECRET
    )
    function signUpWithLine() {
      signInWithLine(signUpSuccess)
    }

    return {
      isMobile,
      stepNum,
      countDownTimer,
      isAgreed,
      isMarketed,
      transitionName,
      isOneTimeEmail,
      waitingToResendTime,
      showCaptcha,
      isEmailVerifyCodeCorrect,
      locale,
      langForDatePicker,
      isUsernameValid,
      usernameInvalidMessage,
      isRegTelValid,

      imageToken,

      captchaToken,
      captchaEKey,

      regUsername,
      regTel,
      regPassword,
      regEmail,
      regBirthday,
      regEmailVerifyCode,
      regCurrency,
      regConfirmPassword,
      regActualName,

      checkEmailVerifyCode,
      checkUsername,
      refreshCaptcha,
      captchaVerify,
      nextStep,
      prevStep,
      errorHandler,
      handleSignUp,
      checkIsOneTimeEmail,
      sendVerifyCode,

      hasTelegramOauth,
      signUpWithTelegram,
      hasGoogleOauth,
      signUpWithGoogle,
      hasLineOauth,
      signUpWithLine
    }
  }
})
</script>
